import * as Sentry from '@sentry/react';

// Category must follow "domain.action" pattern (e.g., 'chart.meta', 'data.loading')
type SentryCategory = `${string}.${string}`;

type SentryLevel = 'debug' | 'info' | 'warning' | 'error' | 'fatal';

type SentryBreadcrumbOptions = {
    category: SentryCategory;
    message: string;
    level?: SentryLevel;
    data?: Record<string, unknown>;
};

// Breadcrumbs are not errors - they provide context that appears in Sentry when a real error occurs later
function sentryAddBreadcrumb({
    category,
    message,
    level = 'info',
    data,
}: SentryBreadcrumbOptions): void {
    Sentry.addBreadcrumb({
        category,
        message,
        level,
        data,
    });
}

type SentryCaptureMessageOptions = {
    message: string;
    level?: SentryLevel;
    context?: Record<string, Record<string, unknown>>;
    tags?: Record<string, string>;
};

function sentryCaptureMessage({
    message,
    level = 'info',
    context,
    tags,
}: SentryCaptureMessageOptions): void {
    Sentry.withScope((scope) => {
        if (tags) {
            Object.entries(tags).forEach(([key, value]) => {
                scope.setTag(key, value);
            });
        }
        if (context) {
            Object.entries(context).forEach(([key, value]) => {
                scope.setContext(key, value);
            });
        }
        Sentry.captureMessage(message, level);
    });
}

// Context persists for the session - use sparingly
function sentrySetContext(name: string, context: Record<string, unknown>): void {
    Sentry.setContext(name, context);
}

// Tags are indexed - keep values low-cardinality (no UUIDs or timestamps)
function sentrySetTag(key: string, value: string): void {
    Sentry.setTag(key, value);
}

export { sentryAddBreadcrumb, sentryCaptureMessage, sentrySetContext, sentrySetTag };

export type {
    SentryBreadcrumbOptions,
    SentryCaptureMessageOptions,
    SentryCategory,
    SentryLevel,
};
